import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../api/users';
import { useTranslation } from 'react-i18next';

type FormData = {
  first_name: string;
  last_name: string;
  phone?: string;
  level?: string;
  preferred_times?: string;
  about_me?: string;
  show_phone_in_match: boolean;
};

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const schema = useMemo(() => z.object({
    first_name: z.string().min(2, t('auth.firstNameRequired')),
    last_name: z.string().min(2, t('auth.lastNameRequired')),
    phone: z.string().optional(),
    level: z.string().optional(),
    preferred_times: z.string().optional(),
    about_me: z.string().optional(),
    show_phone_in_match: z.boolean(),
  }), [t]);

  const LEVEL_OPTIONS = [
    { value: '', label: t('auth.levelSelect') },
    { value: 'Anfänger', label: t('auth.levelBeginner') },
    { value: 'Fortgeschrittener', label: t('auth.levelAdvanced') },
    { value: 'Erfahren', label: t('auth.levelExperienced') },
    { value: 'Wettkampf', label: t('auth.levelCompetition') },
  ];

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      phone: user?.phone ?? '',
      level: user?.level ?? '',
      preferred_times: user?.preferred_times ?? '',
      about_me: user?.about_me ?? '',
      show_phone_in_match: user?.show_phone_in_match ?? false,
    },
  });

  async function onSubmit(data: FormData) {
    setError('');
    try {
      await usersApi.updateMe({
        ...data,
        phone: data.phone || undefined,
        level: data.level || undefined,
      });
      await refreshUser();
      navigate('/profile');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('profile.saveError'));
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.editTitle')}</h1>

      <div className="card">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">{t('profile.firstName')}</label>
              <input type="text" {...register('first_name')} className={`input ${errors.first_name ? 'input-error' : ''}`} />
              {errors.first_name && <p className="error-text">{errors.first_name.message}</p>}
            </div>
            <div>
              <label className="label">{t('profile.lastName')}</label>
              <input type="text" {...register('last_name')} className={`input ${errors.last_name ? 'input-error' : ''}`} />
              {errors.last_name && <p className="error-text">{errors.last_name.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">{t('profile.phoneField')}</label>
            <input type="tel" {...register('phone')} className="input" placeholder="+49 123 456789" />
          </div>

          {/* Handynummer für Terminfindung freigeben */}
          <div className="flex items-start gap-3 p-4 bg-primary-50 border border-primary-100 rounded-lg">
            <input
              type="checkbox"
              id="show_phone_in_match"
              {...register('show_phone_in_match')}
              className="w-4 h-4 mt-0.5 text-primary-600 border-gray-300 rounded flex-shrink-0"
            />
            <label htmlFor="show_phone_in_match" className="text-sm cursor-pointer">
              <span className="font-medium text-gray-900">{t('profile.showPhoneInMatch')}</span>
              <p className="text-gray-500 mt-0.5">{t('profile.showPhoneInMatchHint')}</p>
            </label>
          </div>

          <div>
            <label className="label">{t('profile.levelField')}</label>
            <select {...register('level')} className="input">
              {LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{t('profile.levelEloHint')}</p>
          </div>

          <div>
            <label className="label">{t('profile.preferredTimes')}</label>
            <textarea
              {...register('preferred_times')}
              className="input"
              rows={3}
              placeholder={t('profile.preferredTimesPlaceholder')}
            />
          </div>

          <div>
            <label className="label">{t('profile.aboutMe')}</label>
            <textarea
              {...register('about_me')}
              className="input"
              rows={4}
              placeholder={t('profile.aboutMePlaceholder')}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => navigate('/profile')} className="btn btn-secondary">
              {t('profile.cancel')}
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary">
              {isSubmitting ? t('profile.saving') : t('profile.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
